import { uniqBy } from 'lodash-es';
import { useCallback, useRef, useState } from 'react';
import { unstable_batchedUpdates as batchedUpdates } from 'react-dom';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import {
  list as listApi,
  ListParams,
  updateStatus as updateStatusApi,
  UpdateStatusBody,
} from 'services/api/review';

export const currentReviewProfileState = atom<null | Review.Profile>(null);

export const reviewListState = atom<{
  list: Review.Profile[];
  loading: boolean;
  hasMore: boolean;
}>({
  list: [],
  loading: false,
  hasMore: true,
});

export interface ReviewListFilter {
  status: Review.Status | 0;
  star: 'good' | 'bad' | 'all';
}

export const reviewListFilterState = atom<ReviewListFilter>({
  status: 1,
  star: 'bad',
});

export function useReview() {
  const [hasMore, setHasMore] = useState(true);

  const [list, setList] = useState<Review.Profile[]>([]);

  const [loading, setLoading] = useState(false);

  const fetchFlag = useRef(0);

  const fetchList = useCallback((params: ListParams) => {
    fetchFlag.current += 1;

    const { current: currentFetchFlag } = fetchFlag;

    setLoading(true);

    return listApi(params)
      .then((res) => {
        if (currentFetchFlag !== fetchFlag.current) {
          return;
        }

        const { reviewInfos } = res;

        batchedUpdates(() => {
          setHasMore(reviewInfos.length >= params.pageSize);

          setList((list) => {
            return uniqBy([...list, ...reviewInfos], 'commentId');
          });

          setLoading(false);
        });
      })
      .catch((err) => {
        if (currentFetchFlag === fetchFlag.current) {
          setLoading(false);
        }

        return Promise.reject(err);
      });
  }, []);

  return {
    hasMore,
    list,
    loading,
    setHasMore,
    setList,
    setLoading,
    fetchList,
  };
}

export function useGlobalReview() {
  const fetchFlag = useRef(0);

  const currentReview = useAtomValue(currentReviewProfileState);

  const setCurrentReview = useSetAtom(currentReviewProfileState);

  const reviewList = useAtomValue(reviewListState);

  const setReviewList = useSetAtom(reviewListState);

  const reviewListFilter = useAtomValue(reviewListFilterState);

  const setReviewListFilter = useSetAtom(reviewListFilterState);

  const fetchList = useCallback(
    (params: ListParams) => {
      fetchFlag.current += 1;

      const { current: currentFetchFlag } = fetchFlag;

      setReviewList((preValue) => {
        return {
          ...preValue,
          loading: true,
        };
      });

      return listApi(params)
        .then((res) => {
          if (currentFetchFlag !== fetchFlag.current) {
            return;
          }

          const { reviewInfos } = res;

          setReviewList((preValue) => {
            return {
              list: uniqBy([...preValue.list, ...reviewInfos], 'commentId'),
              loading: false,
              hasMore: reviewInfos.length >= params.pageSize,
            };
          });
        })
        .catch((err) => {
          if (currentFetchFlag === fetchFlag.current) {
            setReviewList((preValue) => {
              return {
                ...preValue,
                loading: false,
              };
            });
          }

          return Promise.reject(err);
        });
    },
    [setReviewList],
  );

  const updateStatus = useCallback(
    (body: UpdateStatusBody) => {
      return updateStatusApi(body).then(() => {
        setReviewList((preValue) => {
          return {
            ...preValue,
            list: preValue.list.map((profile) => {
              if (profile.commentId !== body.commentId) {
                return profile;
              }

              return {
                ...profile,
                status: body.status,
              };
            }),
          };
        });
      });
    },
    [setReviewList],
  );

  return {
    currentReview,
    setCurrentReview,
    reviewList,
    setReviewList,
    reviewListFilter,
    setReviewListFilter,
    fetchList,
    updateStatus,
  };
}
